import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { clearToken, getToken, setToken, UNAUTHORIZED_EVENT } from '../api/client';
import * as endpoints from '../api/endpoints';
import {
  isTwoFactorChallenge,
  type CurrentUser,
  type LoginResponse,
  type TwoFactorMethod,
} from '../api/types';

interface AuthContextValue {
  user: CurrentUser | null;
  /** True until the initial /api/auth/me round-trip settles, so routes do not flash. */
  initialising: boolean;
  isAuthenticated: boolean;
  /**
   * First sign-in step. Resolves to the signed-in user, or - when SMS two-step sign-in is on -
   * to a challenge the caller completes with {@link completeTwoFactor}.
   */
  signIn: (email: string, password: string, rememberMe: boolean) => Promise<SignInStep>;
  /** Second sign-in step: the code from the SMS. */
  completeTwoFactor: (challenge: string, code: string, rememberMe: boolean) => Promise<CurrentUser>;
  signOut: () => Promise<void>;
  /**
   * Re-reads the signed-in user from the API. Needed wherever something the app gates on has
   * changed server-side but the token has not - clearing `mustChangePassword` is the case that
   * forced this to exist.
   */
  refresh: () => Promise<void>;
  /** Convenience wrapper - the sidebar and several pages gate on roles. */
  hasRole: (...roles: string[]) => boolean;
}

export type SignInStep =
  | { kind: 'signedIn'; user: CurrentUser }
  | {
      kind: 'twoFactor';
      challenge: string;
      method: TwoFactorMethod;
      sentTo: string;
      message?: string | null;
    };

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Id of the account most recently signed in on this browser tab. RequireAuth stamps it on the
 * "return to" location when it bounces a signed-out visitor to /login, and the login page only
 * returns there when the SAME account signs back in. Without it, signing out of one account
 * and into another sent the second account to the first one's last page - which it usually has
 * no permission to see.
 */
const LAST_USER_KEY = 'idv.lastUserId';

export function getLastUserId(): number | null {
  try {
    const raw = sessionStorage.getItem(LAST_USER_KEY);
    return raw ? Number(raw) : null;
  } catch {
    return null;
  }
}

function rememberUser(u: CurrentUser | null) {
  if (!u) return;
  try {
    sessionStorage.setItem(LAST_USER_KEY, String(u.id));
  } catch {
    // Storage can be unavailable (private mode); the only cost is a less precise redirect.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [initialising, setInitialising] = useState(true);

  // Rehydrate on boot: a stored token is only trusted once the API confirms the account
  // is still active, which also refreshes role and client name.
  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      if (!getToken()) {
        setInitialising(false);
        return;
      }
      try {
        const me = await endpoints.fetchCurrentUser();
        rememberUser(me);
        if (!cancelled) setUser(me);
      } catch {
        clearToken();
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setInitialising(false);
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  // The axios interceptor raises this when the API rejects the token mid-session.
  useEffect(() => {
    const handler = () => setUser(null);
    window.addEventListener(UNAUTHORIZED_EVENT, handler);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handler);
  }, []);

  const acceptLogin = useCallback((response: LoginResponse) => {
    setToken(response.accessToken, response.expiresAt);
    rememberUser(response.user);
    setUser(response.user);
    return response.user;
  }, []);

  const signIn = useCallback(
    async (email: string, password: string, rememberMe: boolean): Promise<SignInStep> => {
      const response = await endpoints.login({ email, password, rememberMe });
      if (isTwoFactorChallenge(response)) {
        return {
          kind: 'twoFactor',
          challenge: response.challenge,
          method: response.method,
          sentTo: response.sentTo,
          message: response.message,
        };
      }
      return { kind: 'signedIn', user: acceptLogin(response) };
    },
    [acceptLogin],
  );

  const completeTwoFactor = useCallback(
    async (challenge: string, code: string, rememberMe: boolean) =>
      acceptLogin(await endpoints.loginTwoFactor(challenge, code, rememberMe)),
    [acceptLogin],
  );

  const signOut = useCallback(async () => {
    try {
      await endpoints.logout();
    } catch {
      // Logging out is best-effort: no server session exists, so a failure here is harmless.
    }
    clearToken();
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    if (!getToken()) return;
    const me = await endpoints.fetchCurrentUser();
    setUser(me);
  }, []);

  const hasRole = useCallback(
    (...roles: string[]) => (user ? roles.includes(user.role) : false),
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      initialising,
      isAuthenticated: user !== null,
      signIn,
      completeTwoFactor,
      signOut,
      refresh,
      hasRole,
    }),
    [user, initialising, signIn, completeTwoFactor, signOut, refresh, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an AuthProvider');
  return ctx;
}
